'use client';

import type { FieldContent } from '@capture/shared';
import { isOperatorKeyedField, fieldContentName } from '@capture/shared';

/**
 * The derived (non-keyed) subset of a job type's fields — the SINGLE source of
 * truth for "which fields may be a webhook response-mapping target". Filters
 * via the shared `isOperatorKeyedField` predicate (the same one the
 * transform-webhook T2/T4 publish gate uses). Reused by `DerivedFieldSelect`
 * (option list), `WebhookResponseTable` (per-row stale-target flag), and
 * `WebhookBody` (block-save banner) so all three agree by construction.
 */
export function filterDerivedFields(fields: FieldContent[]): FieldContent[] {
  return fields.filter((f) => !isOperatorKeyedField(f));
}

/** True when `target` names a currently-derived field. An empty target is
 *  "unset", not "stale" — callers decide how to treat that separately. */
export function isDerivedTargetName(fields: FieldContent[], target: string): boolean {
  if (!target) return false;
  return filterDerivedFields(fields).some((f) => fieldContentName(f) === target);
}

export interface DerivedFieldSelectProps {
  /**
   * Full field definitions for this job type. Filtered INTERNALLY to exclude
   * operator-keyed fields via `isOperatorKeyedField` — the same predicate the
   * transform-webhook T2/T4 publish gate uses server-side
   * (`validateWebhookTransformConfig` in webhook-transform.ts). A webhook
   * response-mapping target must never be an operator-keyed field, so the
   * filtering lives here (not in a caller-supplied pre-filtered list) to
   * guarantee it can never be bypassed by an oversight upstream.
   */
  fields: FieldContent[];
  /** Selected field's identity — `internalName ?? name` (`fieldContentName`),
   *  matching how the backend gate resolves `responseMappings[].target`. */
  value: string;
  onChange: (internalName: string) => void;
  ariaLabel?: string;
  'data-testid'?: string;
}

/**
 * Reusable derived-field picker for the webhook DERIVATION behavior
 * (transform-webhook T6). Adapts MappingPanel's grouped `<select>` field-picker
 * markup (export/MappingPanel.tsx) — placeholder option + a labeled
 * `<optgroup>` — but sources from full `FieldContent` (not bare names) so it
 * can enforce the non-keyed filter itself.
 */
export function DerivedFieldSelect({
  fields,
  value,
  onChange,
  ariaLabel,
  'data-testid': testId,
}: DerivedFieldSelectProps) {
  const derived = filterDerivedFields(fields);

  return (
    <select
      value={value}
      onChange={(e) => onChange(e.target.value)}
      aria-label={ariaLabel ?? 'Target field'}
      data-testid={testId}
    >
      <option value="">— select field —</option>
      <optgroup label="Derived Fields">
        {derived.map((f) => {
          const name = fieldContentName(f);
          return (
            <option key={name} value={name}>
              {f.label || name}
            </option>
          );
        })}
      </optgroup>
      {/* F-036-style fallback (MappingPanel dossier-field picker): a stored
          target that no longer classifies as derived (e.g. its behaviors
          changed after the row was authored) is preserved as an option so
          the row doesn't silently collapse to a blank selection. */}
      {value && !derived.some((f) => fieldContentName(f) === value) && (
        <option value={value}>{value} (not a derived field)</option>
      )}
    </select>
  );
}
